import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import { userHasFn, SUB_APPS } from '../../../utils/rbac';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const tabs = ['zones', 'groups', 'questions', 'toggles'];

function HomesiteAdmin() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('zones');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [zones, setZones] = useState([]);
  const [zoneIdForGroup, setZoneIdForGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupIdForQuestion, setGroupIdForQuestion] = useState('');
  const [questions, setQuestions] = useState([]);
  const [flatQuestions, setFlatQuestions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [togglePropertyId, setTogglePropertyId] = useState('');
  const [toggles, setToggles] = useState([]);
  const [toggleDirty, setToggleDirty] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState({ type: null, payload: null });

  useEffect(() => {
    if (user && !userHasFn(user, SUB_APPS.HOMESITE, 'admin')) navigate(ROUTES.HOMESITE);
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const tRes = await axios.get('/api/homesite/admin/templates', { headers: authHeader() });
        setTemplates(tRes.data);
        const h = tRes.data.find((t) => t.slug === 'homesite-inspections') || tRes.data[0];
        if (h) setTemplateId(String(h.id));
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      }
    })();
  }, []);

  const loadProperties = async () => {
    const res = await axios.get('/api/properties', { headers: authHeader() });
    setProperties(res.data);
  };
  const loadZones = async () => {
    if (!templateId) return;
    const res = await axios.get('/api/homesite/admin/zones', { headers: authHeader(), params: { templateId } });
    setZones(res.data);
  };
  const loadGroups = async () => {
    if (!zoneIdForGroup) return setGroups([]);
    const res = await axios.get('/api/homesite/admin/groups', { headers: authHeader(), params: { zoneId: zoneIdForGroup } });
    setGroups(res.data);
  };
  const loadQuestions = async () => {
    if (!groupIdForQuestion) return setQuestions([]);
    const res = await axios.get('/api/homesite/admin/questions', { headers: authHeader(), params: { groupId: groupIdForQuestion } });
    setQuestions(res.data);
  };
  const loadFlatQuestions = async () => {
    if (!templateId) return;
    const res = await axios.get('/api/homesite/admin/questions/flat', { headers: authHeader(), params: { templateId } });
    setFlatQuestions(res.data);
  };
  const loadToggles = async () => {
    if (!togglePropertyId || !templateId) return setToggles([]);
    const res = await axios.get(`/api/homesite/admin/properties/${togglePropertyId}/toggles`, {
      headers: authHeader(),
      params: { templateId },
    });
    setToggles(res.data.map((r) => ({ ...r, enabled: r.enabled })));
    setToggleDirty(false);
  };

  useEffect(() => {
    loadZones().catch(() => {});
  }, [templateId]);
  useEffect(() => {
    if (tab === 'groups' || tab === 'questions') loadGroups().catch(() => {});
  }, [tab, zoneIdForGroup]);
  useEffect(() => {
    if (tab === 'questions') {
      loadQuestions().catch(() => {});
      loadFlatQuestions().catch(() => {});
    }
  }, [tab, groupIdForQuestion, templateId]);
  useEffect(() => {
    if (tab === 'toggles') {
      loadProperties().catch(() => {});
      loadToggles().catch(() => {});
    }
  }, [tab, togglePropertyId, templateId]);

  const saveToggles = async () => {
    setError('');
    try {
      await axios.patch(
        `/api/homesite/admin/properties/${togglePropertyId}/toggles`,
        { toggles: toggles.map((t) => ({ questionId: t.question_id, enabled: t.enabled })) },
        { headers: authHeader() }
      );
      setMsg('Toggles saved');
      setToggleDirty(false);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const setToggle = (questionId, enabled) => {
    setToggles((prev) => prev.map((t) => (t.question_id === questionId ? { ...t, enabled } : t)));
    setToggleDirty(true);
  };

  if (!user || !userHasFn(user, SUB_APPS.HOMESITE, 'admin')) return <p className="text-muted">Admin only.</p>;

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Homesite admin</h2>
        <p className="text-muted small mb-0">Zones, groups, questions, and per-property toggles for the homesite template.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="card app-card">
        <div className="card-body">
      <div className="mb-3">
        <label className="form-label small mb-1">Template</label>
        <select className="form-select form-select-sm w-auto" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <div className="btn-group flex-wrap mb-4">
        {tabs.map((t) => (
          <button key={t} type="button" className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'zones' && (
        <SimpleList
          title="Zones"
          rows={zones}
          onAdd={() => setModal({ type: 'zone', payload: { templateId: Number(templateId) } })}
          onEdit={(r) => setModal({ type: 'zone', payload: r })}
          columns={['name', 'sort_order', 'is_active']}
        />
      )}
      {tab === 'groups' && (
        <>
          <select className="form-select form-select-sm w-auto mb-2" value={zoneIdForGroup} onChange={(e) => setZoneIdForGroup(e.target.value)}>
            <option value="">Select zone...</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <SimpleList
            title="Groups"
            rows={groups}
            onAdd={() => setModal({ type: 'group', payload: { zoneId: Number(zoneIdForGroup) } })}
            onEdit={(r) => setModal({ type: 'group', payload: r })}
            columns={['name', 'sort_order', 'is_active']}
          />
        </>
      )}
      {tab === 'questions' && (
        <>
          <select className="form-select form-select-sm w-auto mb-2" value={zoneIdForGroup} onChange={(e) => { setZoneIdForGroup(e.target.value); setGroupIdForQuestion(''); }}>
            <option value="">Select zone...</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <select className="form-select form-select-sm w-auto mb-2" value={groupIdForQuestion} onChange={(e) => setGroupIdForQuestion(e.target.value)}>
            <option value="">Select group...</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <SimpleList
            title="Questions"
            rows={questions}
            onAdd={() => setModal({ type: 'question', payload: { groupId: Number(groupIdForQuestion) } })}
            onEdit={(r) => setModal({ type: 'question', payload: r })}
            columns={['question_text', 'sort_order', 'is_active']}
          />
          <h6 className="text-muted mt-3">All questions in template</h6>
          <div className="app-table-wrap table-responsive" style={{ maxHeight: 240, overflow: 'auto' }}>
            <table className="table table-sm mb-0">
              <thead className="table-light"><tr><th>Zone</th><th>Group</th><th>Question</th></tr></thead>
              <tbody>
                {flatQuestions.map((q) => <tr key={q.id}><td>{q.zone_name}</td><td>{q.group_name}</td><td>{q.question_text}</td></tr>)}
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab === 'toggles' && (
        <div>
          <select className="form-select form-select-sm w-auto mb-2" value={togglePropertyId} onChange={(e) => setTogglePropertyId(e.target.value)}>
            <option value="">Select property...</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="app-table-wrap table-responsive" style={{ maxHeight: 420, overflow: 'auto' }}>
            <table className="table table-sm mb-0">
              <thead className="table-light"><tr><th>Zone / Group</th><th>Question</th><th>On</th></tr></thead>
              <tbody>
                {toggles.map((t) => (
                  <tr key={t.question_id}>
                    <td>{t.zone_name} / {t.group_name}</td>
                    <td>{t.question_text}</td>
                    <td><input type="checkbox" className="form-check-input" checked={!!t.enabled} onChange={(e) => setToggle(t.question_id, e.target.checked)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn btn-primary btn-sm mt-2" disabled={!toggleDirty} onClick={saveToggles}>Save toggles</button>
        </div>
      )}

        </div>
      </div>

      <AdminModal
        modal={modal}
        onClose={() => setModal({ type: null, payload: null })}
        templateId={templateId}
        zoneIdForGroup={zoneIdForGroup}
        groupIdForQuestion={groupIdForQuestion}
        onSaved={() => { loadZones(); loadGroups(); loadQuestions(); loadFlatQuestions(); }}
        setError={setError}
      />
    </div>
  );
}

function SimpleList({ title, rows, onAdd, onEdit, columns }) {
  return (
    <div>
      <button type="button" className="btn btn-success btn-sm mb-2" onClick={onAdd}>Add {title.slice(0, -1)}</button>
      <div className="app-table-wrap table-responsive">
        <table className="table table-sm mb-0">
          <thead className="table-light">
            <tr>{columns.map((c) => <th key={c}>{c}</th>)}<th /></tr>
          </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              {columns.map((c) => <td key={c}>{String(r[c])}</td>)}
              <td><button type="button" className="btn btn-link btn-sm p-0" onClick={() => onEdit(r)}>Edit</button></td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminModal({ modal, onClose, templateId, zoneIdForGroup, groupIdForQuestion, onSaved, setError }) {
  const type = modal?.type;
  const payload = modal?.payload;
  const [form, setForm] = useState({});
  useEffect(() => setForm(payload ? { ...payload } : {}), [payload, type]);
  if (!type) return null;

  const save = async () => {
    setError('');
    try {
      if (type === 'zone') {
        if (form.id) {
          await axios.put(`/api/homesite/admin/zones/${form.id}`, { name: form.name, description: form.description, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        } else {
          await axios.post('/api/homesite/admin/zones', { templateId: form.templateId || Number(templateId), name: form.name, description: form.description, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        }
      } else if (type === 'group') {
        if (form.id) {
          await axios.put(`/api/homesite/admin/groups/${form.id}`, { name: form.name, description: form.description, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        } else {
          await axios.post('/api/homesite/admin/groups', { zoneId: form.zoneId || Number(zoneIdForGroup), name: form.name, description: form.description, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        }
      } else if (type === 'question') {
        if (form.id) {
          await axios.put(`/api/homesite/admin/questions/${form.id}`, { questionText: form.question_text, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        } else {
          await axios.post('/api/homesite/admin/questions', { groupId: form.group_id || Number(groupIdForQuestion), questionText: form.question_text, sortOrder: Number(form.sort_order) || 0, isActive: form.is_active !== false }, { headers: authHeader() });
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{type}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label">{type === 'question' ? 'Question' : 'Name'}</label>
              {type === 'question' ? (
                <textarea className="form-control" rows={3} value={form.question_text || ''} onChange={(e) => setForm((f) => ({ ...f, question_text: e.target.value }))} />
              ) : (
                <input className="form-control" value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              )}
            </div>
            {type !== 'question' && (
              <div className="mb-2">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
            )}
            <div className="mb-2">
              <label className="form-label">Sort order</label>
              <input type="number" className="form-control" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
            </div>
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id="activeToggle" checked={form.is_active !== false} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              <label className="form-check-label" htmlFor="activeToggle">Active</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomesiteAdmin;
